/**
 * lib-components
 *
 * Adapptio library to help creating components.
 *
 * @package lib-components
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { CSSProperties, useRef, useEffect, useState } from "react";
import {
	COMPONENT_MODE,
	ISchemaComponentList,
	ISchemaComponentListSpec,
	ISchemaComponentModel,
	ISchemaConstObject,
	TGenericComponentInstance,
	TGetBlueprintSchemaModel,
	TGetBlueprintSchemaSpec,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import { isFunction, isNonEmptyArray, isValidObject, offEvent, onEvent } from "@adapptio/lib-shared";
import { Component } from "./ViewComponent";
import { ClassList } from "../Classes/ClassList";
import {
	IAllowedResizeDimensions,
	TResizeBeginHandler,
	TResizeEndHandler,
	TResizeUpdateHandler
} from "../Editor/IResize";
import { DROP_ZONE_MODE, useComponentListDnD } from "../Editor/useComponentListDnD";
import { IElementDimensions, IElementOffset } from "../Editor/IEditCommon";
import { TCanDropFunction } from "../Editor/EditDnD";
import { useEditContext } from "../Editor/EditContext";
import { useDnDStateChangeHandler } from "../Hooks/useDnDStateChangeHandler";
import { IBaseProps } from "../ReactComponents/props";
import { CSS_MEDIA_classNames } from "../Enums/CSS_MEDIA_CLASS";
import { useComponentMainContext } from "./ViewComponentContext";

/**
 * Component type list element
 */
export interface IComponentListElementComponent<TInheritedProps extends TSchemaConstObjectProps> {
	type: "component";
	cmpInstance: TGenericComponentInstance<TInheritedProps>;
	srcIndex: number;
	dropIndex?: number;
}

/**
 * Placeholder type list element
 */
export interface IComponentListElementPlaceholder<
	TInheritedProps extends TSchemaConstObjectProps,
	TPlaceholderMetaData = never
> {
	type: "placeholder";
	nodeId: number;
	inheritedProps: TGetBlueprintSchemaSpec<ISchemaConstObject<TInheritedProps>>;
	dimensions: IElementDimensions;
	offset: IElementOffset;
	srcIndexRef?: number;
	metaData?: TPlaceholderMetaData;
}

/**
 * Component list element
 */
export type TComponentListElement<
	TInheritedProps extends TSchemaConstObjectProps,
	TPlaceholderMetaData = never
> =
	| IComponentListElementComponent<TInheritedProps>
	| IComponentListElementPlaceholder<TInheritedProps, TPlaceholderMetaData>;

/**
 *  Component list properties
 */
export interface IComponentListProps<
	TInheritedProps extends TSchemaConstObjectProps = Record<string, never>,
	TPlaceholderMetaData = never
> extends IBaseProps {
	/** Component instances */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	components: ISchemaComponentListSpec<TInheritedProps>;

	/** React tag to wrap component into (defaults to <div>) */
	tagName?: "div" | "span" | "ol" | "ul" | "td";

	/** Override tag property passed to child components */
	childTagName?: "div" | "span" | "li";

	/** ClassList to pass to children */
	childClassList?:
		| ClassList
		| ((
				element?: TComponentListElement<TInheritedProps, TPlaceholderMetaData>,
				index?: number
		) => ClassList);

	/** ClassList to pass to child components */
	childComponentClassList?:
		| ClassList
		| ((
				element: TComponentListElement<TInheritedProps, TPlaceholderMetaData>,
				index: number
		) => ClassList);

	/** Custom style properties to pass to child wrappers */
	childInlineStyle?:
		| CSSProperties
		| ((
				element: TComponentListElement<TInheritedProps, TPlaceholderMetaData>,
				index: number
		) => CSSProperties);

	/** Custom style properties to pass to child components */
	childComponentInlineStyle?:
		| CSSProperties
		| ((
				element: TComponentListElement<TInheritedProps, TPlaceholderMetaData>,
				index: number
		) => CSSProperties);

	/** Child additional properties */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	childComponentAdditionalProps?:
		| any
		| ((
				element: TComponentListElement<TInheritedProps, TPlaceholderMetaData>,
				index: number
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
		) => any);

	/** If element can be resized in edit mode */
	allowResize?:
		| IAllowedResizeDimensions
		| ((
				componentInstance: TGenericComponentInstance<TInheritedProps>,
				index: number
		) => IAllowedResizeDimensions);

	/** Callback to handle component resize begin */
	onResizeBegin?: TResizeBeginHandler<TGenericComponentInstance<TInheritedProps>, unknown>;

	/** Callback to handle component resize update on mouse move (should update element style) */
	onResizeUpdate?: TResizeUpdateHandler<TGenericComponentInstance<TInheritedProps>, unknown>;

	/** Callback to handle component resize end (should write changes to model) */
	onResizeEnd?: TResizeEndHandler<TGenericComponentInstance<TInheritedProps>, unknown>;

	/** Function which can modify item model when a component is being dropped */
	modifyModelOnDrop?: (
		itemModel: ISchemaComponentModel,
		element: IComponentListElementPlaceholder<TInheritedProps, TPlaceholderMetaData>
	) => void;

	/* Function to determine if a new target is a valid drop zone */
	canDrop?: TCanDropFunction;

	/** How to calculate drop position with respect to sibling elements */
	dropZoneMode?: DROP_ZONE_MODE;

	/** Drop text */
	dropText?: string;

	/** Component list model node */
	modelNode?: TGetBlueprintSchemaModel<ISchemaComponentList<TInheritedProps>>;
}

/**
 * Renders a list of  Components
 *
 * @param reactProps React component props
 */
export function ComponentList<
	TInheritedProps extends TSchemaConstObjectProps = Record<string, never>,
	TPlaceholderMetaData = never
>(reactProps: IComponentListProps<TInheritedProps, TPlaceholderMetaData>): React.ReactElement {
	const componentMainContext = useComponentMainContext();
	const inEditor = componentMainContext.rCtx.isInEditor();

	const editCtx = useEditContext();
	const elementRef = useRef<HTMLElement>();

	const { tagName = "div", componentPath, componentMode, dropText = "Drop components here." } = reactProps;

	const editComponentMode = componentMode === COMPONENT_MODE.EDIT;

	// Use DnD

	const { handleDragOver, handleItemDragOver, handleDrop, elements } = useComponentListDnD<
		TInheritedProps,
		TPlaceholderMetaData
	>({
		components: reactProps.components,
		listElementRef: elementRef,
		dropZoneMode: reactProps.dropZoneMode || DROP_ZONE_MODE.APPEND,
		modelNode: reactProps.modelNode,
		modifyModelOnDrop: reactProps.modifyModelOnDrop,
		canDrop: reactProps.canDrop
	});

	// Handle selection

	const [ , setSelectionRev ] = useState(0); // Just dummy state to trigger re-render on selection change

	useEffect(() => {
		if (!editCtx) {
			return;
		}

		const handleSelectionChange = () => setSelectionRev((x) => x + 1);

		onEvent(editCtx.selection.onChange, handleSelectionChange);

		return () => {
			offEvent(editCtx.selection.onChange, handleSelectionChange);
		};
	}, [ elements, editCtx ]);

	// Create classList

	const { classList } = ClassList.getElementClassListAndIdClassName("adpt-component-list", componentPath, {
		componentClassList: reactProps.classList,
		componentMode
	});

	// Content items

	const content = elements.map((element, index) => {
		const key = element.type === "component" ? `cmp_${element.cmpInstance.uid}` : `ph_${element.nodeId}`;

		return (
			<ComponentListItem<TInheritedProps, TPlaceholderMetaData>
				key={key}
				element={element}
				index={index}
				tagName={reactProps.childTagName}
				classList={reactProps.childClassList}
				componentClassList={reactProps.childComponentClassList}
				inlineStyle={reactProps.childInlineStyle}
				componentInlineStyle={reactProps.childComponentInlineStyle}
				componentAdditionalProps={reactProps.childComponentAdditionalProps}
				inEditor={inEditor}
				allowResize={reactProps.allowResize}
				onResizeBegin={reactProps.onResizeBegin}
				onResizeUpdate={reactProps.onResizeUpdate}
				onResizeEnd={reactProps.onResizeEnd}
				handleItemDragOver={handleItemDragOver}
				handleDrop={handleDrop}
			/>
		);
	});

	const contentEmpty = !content.length;

	// Drop text

	const [ dropActive, setDropActive ] = useState(editComponentMode && contentEmpty);

	useDnDStateChangeHandler(editCtx, (active) => {
		if (!elementRef.current) {
			return;
		}

		setDropActive(editComponentMode && active);
	});

	if (editComponentMode && (dropActive || contentEmpty)) {
		const dropClassList = new ClassList("adpt-component-list__drop");

		if (ClassList.isClassList(reactProps.childClassList)) {
			dropClassList.add(...reactProps.childClassList);
		} else if (isFunction(reactProps.childClassList)) {
			dropClassList.add(...reactProps.childClassList());
		}

		content.push(
			<div key="empty" className={dropClassList.toClassName()}>
				<div className="adpt-component-list__drop-text">{dropText}</div>
			</div>
		);
	}

	return React.createElement(
		tagName,
		{
			ref: elementRef,
			className: classList.toClassName(),
			onDragOver: handleDragOver,
			onDrop: handleDrop
		},
		content
	);
}

/**
 *  Component List Item props
 */
interface IComponentListItemProps<
	TInheritedProps extends TSchemaConstObjectProps = Record<string, never>,
	TPlaceholderMetaData = never
> {
	/** Element */
	element: TComponentListElement<TInheritedProps, TPlaceholderMetaData>;

	/** Index */
	index: number;

	/** Tag name */
	tagName?: "div" | "span" | "li";

	/** ClassList */
	classList?:
		| ClassList
		| ((
				element?: TComponentListElement<TInheritedProps, TPlaceholderMetaData>,
				index?: number
		) => ClassList);

	/** ClassList to component */
	componentClassList?:
		| ClassList
		| ((
				element: TComponentListElement<TInheritedProps, TPlaceholderMetaData>,
				index: number
		) => ClassList);

	/** Custom style properties */
	inlineStyle?:
		| CSSProperties
		| ((
				element: TComponentListElement<TInheritedProps, TPlaceholderMetaData>,
				index: number
		) => CSSProperties);

	/** Custom style properties to pass to component */
	componentInlineStyle?:
		| CSSProperties
		| ((
				element: TComponentListElement<TInheritedProps, TPlaceholderMetaData>,
				index: number
		) => CSSProperties);

	/** Component additional properties */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	componentAdditionalProps?:
		| any
		| ((
				element: TComponentListElement<TInheritedProps, TPlaceholderMetaData>,
				index: number
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
		) => any);

	/** In editor */
	inEditor?: boolean;

	/** If element can be resized in edit mode */
	allowResize?:
		| IAllowedResizeDimensions
		| ((
				componentInstance: TGenericComponentInstance<TInheritedProps>,
				index: number
		) => IAllowedResizeDimensions);

	/** Callback to handle component resize begin */
	onResizeBegin?: TResizeBeginHandler<TGenericComponentInstance<TInheritedProps>, unknown>;

	/** Callback to handle component resize update on mouse move (should update element style) */
	onResizeUpdate?: TResizeUpdateHandler<TGenericComponentInstance<TInheritedProps>, unknown>;

	/** Callback to handle component resize end (should write changes to model) */
	onResizeEnd?: TResizeEndHandler<TGenericComponentInstance<TInheritedProps>, unknown>;

	/** Item drag over handler */
	// eslint-disable-next-line max-len
	handleItemDragOver: (
		element: TComponentListElement<TInheritedProps, never>,
		ev: React.DragEvent<HTMLElement>,
		elRef: React.MutableRefObject<HTMLElement>
	) => void;

	/** Drop handler */
	handleDrop: (ev: React.DragEvent<HTMLElement>) => boolean;
}

/**
 *  Component List Item
 */
export function ComponentListItem<
	TInheritedProps extends TSchemaConstObjectProps = Record<string, never>,
	TPlaceholderMetaData = never
>(reactProps: IComponentListItemProps<TInheritedProps, TPlaceholderMetaData>): React.ReactElement {
	const { element, index, tagName = "div", inEditor = false } = reactProps;

	const editContext = useEditContext();

	const elementRef = useRef<HTMLElement>();

	const [ , setRev ] = useState(0);

	useEffect(() => {
		if (element?.type === "component" && element.cmpInstance) {
			const handleChange = () => {
				setRev((v) => v + 1);
			};

			onEvent(element.cmpInstance.onChange, handleChange);

			return () => {
				offEvent(element.cmpInstance.onChange, handleChange);
			};
		}
	}, [ element ]);

	const classList = new ClassList("adpt-component-list__item");

	if (ClassList.isClassList(reactProps.classList)) {
		classList.add(...reactProps.classList);
	} else if (isFunction(reactProps.classList)) {
		classList.add(...reactProps.classList(element, index));
	}

	classList.addModifiers(
		{
			application: !inEditor,
			editor: inEditor
		},
		"element"
	);

	switch (element.type) {
		case "component":
			if (
				editContext &&
				editContext.selection.isComponentSelectedWithRefUpdate(
					element.cmpInstance as TGenericComponentInstance
				)
			) {
				classList.add("adpt-component-list__item--selected");
			}
			break;

		case "placeholder":
			classList.add("adpt-component-list__placeholder");
			break;
	}

	const componentClassList = ClassList.isClassList(reactProps.componentClassList)
		? reactProps.componentClassList
		: isFunction(reactProps.componentClassList)
		? reactProps.componentClassList(element, index)
		: new ClassList();

	const inlineStyle = isFunction(reactProps.inlineStyle)
		? reactProps.inlineStyle(element, index)
		: reactProps.inlineStyle;

	const componentInlineStyle = isFunction(reactProps.componentInlineStyle)
		? reactProps.componentInlineStyle(element, index)
		: reactProps.componentInlineStyle;

	const componentAdditionalProps = isFunction(reactProps.componentAdditionalProps)
		? reactProps.componentAdditionalProps(element, index)
		: reactProps.componentAdditionalProps;

	let onDragOver: (ev: React.DragEvent<HTMLElement>) => void;

	let content = null;

	if (element.type === "component") {
		onDragOver = (ev) => reactProps.handleItemDragOver(element, ev, elementRef);

		const editComponentMode = element.cmpInstance.componentMode === COMPONENT_MODE.EDIT;

		const { mediaResolution } = element.cmpInstance.display;

		// Resolve media query classes

		const mediaResolutionKeys = isValidObject(mediaResolution) ? Object.keys(mediaResolution) : null;

		if (isNonEmptyArray(mediaResolutionKeys)) {
			const selectedMediaResolutions = mediaResolutionKeys.filter((item) => mediaResolution[item]);

			if (mediaResolutionKeys.length !== selectedMediaResolutions.length) {
				const mediaResolutionClassNames = selectedMediaResolutions.map(
					(item) => CSS_MEDIA_classNames[item]
				);

				if (mediaResolutionClassNames.length) {
					classList.add(...mediaResolutionClassNames);
				} else {
					classList.add(CSS_MEDIA_classNames.none);
				}
			}
		}

		content = (
			<Component
				componentInstance={element.cmpInstance as TGenericComponentInstance}
				classList={componentClassList}
				inlineStyle={componentInlineStyle}
				additionalProps={componentAdditionalProps}
				allowResize={
					editComponentMode
						? typeof reactProps.allowResize === "function"
							? reactProps.allowResize(element.cmpInstance, index)
							: reactProps.allowResize
						: null
				}
				onResizeBegin={
					reactProps.onResizeBegin as TResizeBeginHandler<TGenericComponentInstance, unknown>
				}
				onResizeUpdate={
					reactProps.onResizeUpdate as TResizeUpdateHandler<TGenericComponentInstance, unknown>
				}
				onResizeEnd={reactProps.onResizeEnd as TResizeEndHandler<TGenericComponentInstance, unknown>}
				allowDrag={editComponentMode}
			/>
		);
	} else if (element.type === "placeholder") {
		onDragOver = (ev) =>
			reactProps.handleItemDragOver(
				element as TComponentListElement<TInheritedProps>,
				ev,
				elementRef
			);

		content = <div className={componentClassList.toClassName()} style={componentInlineStyle} />;
	}

	if (content) {
		return React.createElement(
			tagName,
			{
				ref: elementRef,
				className: classList.toClassName(),
				style: inlineStyle,
				onDragOver,
				onDrop: reactProps.handleDrop
			},
			content
		);
	}

	return null;
}
